/**
 * The explain trace — why a rule did or did not match.
 *
 * The OpenAPI spec types `DryRunOutDTO.explain` as `{}` (no schema at all), so there is nothing
 * generated to lean on. This mirrors `engine/Explain.kt`, but every renderer treats it as
 * untrusted: an unrecognised node falls back to raw JSON rather than throwing.
 */

import { isRelativeTimeValue, RelativeTimeValue, relativeTimeText, RuleValue } from './ruleAst';

/**
 * `type` is a free string on the backend. These are the values it currently produces — `ALWAYS`
 * is what a rule with no condition explains as, and is easy to miss because the assignment does
 * not mention it.
 */
export type ExplainType = 'AND' | 'OR' | 'NOT' | 'COMPARISON' | 'ALWAYS' | string;

export interface ExplainNode {
  type: ExplainType;
  result: boolean;
  children?: ExplainNode[];
  fact?: string;
  operator?: string;
  expected?: unknown;
  /** A duration arrives readable already (`"28 hours"`), so `actual` needs no unit handling here. */
  actual?: unknown;
  /**
   * The arguments the fact was measured with, defaults filled in — `{ first: 'max', last: 'min' }`.
   * Null on facts without arguments, absent on log rows written before facts could take any.
   */
  args?: Record<string, string> | null;
  /**
   * Set when the comparison could not be made at all — unknown fact, bad value shape. Undocumented
   * in the assignment but the most diagnostic field of the lot, so it is always rendered.
   */
  note?: string;
}

export const isExplainNode = (value: unknown): value is ExplainNode => {
  if (typeof value !== 'object' || value === null) return false;
  const node = value as Partial<ExplainNode>;
  return typeof node.type === 'string' && typeof node.result === 'boolean';
};

export const explainChildren = (node: ExplainNode): ExplainNode[] => (Array.isArray(node.children) ? node.children.filter(isExplainNode) : []);

/**
 * Renders a fact value for display. Values arrive as raw JSON — scalars, `{value, unit}` quantity
 * objects, lists — so this is deliberately total rather than exhaustive.
 *
 * `nameOf` turns an entity reference into its name: `expedition.labels` holds label ids, which say
 * nothing to a reader. A string it does not know is shown as-is — better the raw id than a blank
 * while the list loads or after the row is gone.
 */
export const formatExplainValue = (value: unknown, nameOf?: (id: string) => string | undefined): string => {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.map(item => formatExplainValue(item, nameOf)).join(', ');
  if (typeof value === 'object') {
    // `now + 2 hours` carries a value and a unit too, so it is told apart first.
    if (isRelativeTimeValue(value as RuleValue)) return relativeTimeText(value as RelativeTimeValue);
    const object = value as Record<string, unknown>;
    if ('value' in object) {
      const unit = (object.unit ?? object.currency) as string | undefined;
      return unit ? `${object.value} ${unit}` : String(object.value);
    }
    return JSON.stringify(value);
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') {
    if (!nameOf) return value;
    // Executions stored before RD-3017 hold a LIST fact's `actual` as Kotlin's `toString()` — the
    // one string `[id1, id2]` — so it has to be split before it can be named. Newer ones send an array.
    const listItems = /^\[(.*)]$/s.exec(value)?.[1];
    if (listItems !== undefined) return listItems.split(', ').map(item => nameOf(item) ?? item).join(', ');
    return nameOf(value) ?? value;
  }
  return String(value);
};
